// NotesApp source file: trash.component.ts
//
// Trash page (tasks/delete_trash_client.md): notes moved to Trash, kept by the server for 30 days.
// Restore, Delete permanently (single + selected), Empty Trash. Trash lives on the server only, so
// this page needs a connection; everything else about deletion (offline tombstones, sync) is handled
// by DataService / SyncCoordinatorService.

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subscription, firstValueFrom } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';

import { ApiResponse, DataService, TrashBulkResult, TrashItem } from '../../_service/data.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { SpaceDirectoryService } from '../../_service/space-directory.service';
import { Space } from '../../_service/space.service';
import { EnvironmentService } from '../../_service/sync/environment.service';
import { SyncCoordinatorService } from '../../_service/sync/sync-coordinator.service';
import { NoteDisplayTitlePipe, getNoteDisplayTitle } from '../../_util/note-display-title.util';
import { TRASH_RETENTION_DAYS, formatDeletedAgo, formatRetentionRemaining } from '../../_util/trash.util';

type LoadState = 'loading' | 'ready' | 'error' | 'offline';

@Component({
  selector: 'app-trash',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    NoteDisplayTitlePipe,
  ],
  templateUrl: './trash.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrashComponent implements OnInit, OnDestroy {
  private readonly dataService = inject(DataService);
  private readonly snackbar = inject(SnackbarService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly spaceDirectory = inject(SpaceDirectoryService);
  private readonly environment = inject(EnvironmentService);
  private readonly syncCoordinator = inject(SyncCoordinatorService);
  private readonly router = inject(Router);
  private onlineSub?: Subscription;

  readonly retentionDays = TRASH_RETENTION_DAYS;
  /** Reactive so Space names fill in once the Space list (re)loads. */
  private readonly spaces = toSignal(this.spaceDirectory.spaces$, { initialValue: [] as Space[] });

  readonly state = signal<LoadState>('loading');
  readonly errorMessage = signal('');
  readonly items = signal<TrashItem[]>([]);
  readonly search = signal('');
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  /** Notes with a request in flight (their row buttons are disabled). */
  readonly busyIds = signal<ReadonlySet<string>>(new Set());
  /** A bulk request (selected / Empty Trash) is in flight: every action is disabled. */
  readonly bulkBusy = signal(false);

  /** Newest deletion first, narrowed by the search box (title, type, status, Space). */
  readonly visibleItems = computed(() => {
    const query = this.search().trim().toLowerCase();
    const sorted = [...this.items()].sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? ''));
    if (!query) return sorted;
    return sorted.filter((item) =>
      [this.titleOf(item), item.NoteType, item.Status, this.spaceName(item)]
        .some((value) => String(value ?? '').toLowerCase().includes(query)),
    );
  });

  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly allVisibleSelected = computed(() => {
    const visible = this.visibleItems();
    const selected = this.selectedIds();
    return visible.length > 0 && visible.every((item) => selected.has(item._id));
  });

  ngOnInit(): void {
    this.spaceDirectory.loadCached();
    if (this.environment.isOnline()) this.spaceDirectory.refresh();
    // Reload when the connection comes back (Trash is server-side only).
    this.onlineSub = this.environment.online$.subscribe((online) => {
      if (online && (this.state() === 'offline' || this.state() === 'loading')) void this.load();
      else if (!online && this.state() !== 'ready') this.state.set('offline');
    });
  }

  ngOnDestroy(): void {
    this.onlineSub?.unsubscribe();
  }

  async load(): Promise<void> {
    this.state.set('loading');
    const resp = await firstValueFrom(this.dataService.getTrash());
    if (resp.status === 'error') {
      this.errorMessage.set(resp.error?.message ?? 'Loading Trash failed.');
      this.state.set(resp.error?.code === 0 && !this.environment.isOnline() ? 'offline' : 'error');
      return;
    }
    this.items.set(Array.isArray(resp.data) ? resp.data : []);
    this.pruneSelection();
    this.state.set('ready');
  }

  // ---------------------------------------------------------------- display

  titleOf(item: TrashItem): string {
    return getNoteDisplayTitle(item.Title, undefined);
  }

  deletedLabel(item: TrashItem): string {
    return formatDeletedAgo(item.deletedAt);
  }

  retentionLabel(item: TrashItem): string {
    return formatRetentionRemaining(item.deletedAt);
  }

  deletedAtTooltip(item: TrashItem): string {
    const date = item.deletedAt ? new Date(item.deletedAt) : null;
    return date && !Number.isNaN(date.getTime()) ? `Deleted ${date.toLocaleString()}` : '';
  }

  spaceName(item: TrashItem): string {
    this.spaces(); // dependency: re-evaluate when the Space list arrives
    return this.spaceDirectory.getSpaceName(item.spaceId);
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  isBusy(id: string): boolean {
    return this.bulkBusy() || this.busyIds().has(id);
  }

  // ---------------------------------------------------------------- selection

  toggleSelected(id: string, selected?: boolean): void {
    const next = new Set(this.selectedIds());
    const shouldSelect = selected ?? !next.has(id);
    if (shouldSelect) next.add(id);
    else next.delete(id);
    this.selectedIds.set(next);
  }

  toggleAllVisible(selected: boolean): void {
    const next = new Set(this.selectedIds());
    for (const item of this.visibleItems()) {
      if (selected) next.add(item._id);
      else next.delete(item._id);
    }
    this.selectedIds.set(next);
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  // ---------------------------------------------------------------- single-note actions

  async restore(item: TrashItem): Promise<void> {
    if (this.isBusy(item._id)) return;
    this.setBusy(item._id, true);
    try {
      const resp = await firstValueFrom(this.dataService.restoreFromTrash(item));
      if (resp.status === 'error') {
        this.snackbar.showErrorMessage(this.restoreErrorMessage(resp));
        if (resp.error?.code === 404) this.removeItems([item._id]); // no longer in Trash
        return;
      }
      this.removeItems([item._id]);
      this.snackbar.showSuccessMessage('Note restored');
      this.pullRestoredNotes();
    } finally {
      this.setBusy(item._id, false);
    }
  }

  confirmPermanentDelete(item: TrashItem): void {
    if (this.isBusy(item._id)) return;
    this.confirmation.confirm({
      header: 'Delete permanently',
      message: `Permanently delete "${this.titleOf(item)}"? This action cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete permanently',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => void this.permanentlyDelete(item),
    });
  }

  async permanentlyDelete(item: TrashItem): Promise<void> {
    if (this.isBusy(item._id)) return;
    this.setBusy(item._id, true);
    try {
      const resp = await firstValueFrom(this.dataService.permanentlyDeleteFromTrash(item));
      if (resp.status === 'error') {
        if (resp.error?.code === 404) {
          // Already gone (idempotent on the server): the end state the user asked for.
          this.removeItems([item._id]);
          this.snackbar.showSuccessMessage('Note permanently deleted');
          return;
        }
        this.snackbar.showErrorMessage(resp.error?.message ?? 'Deleting the note permanently failed.');
        return;
      }
      this.removeItems([item._id]);
      this.snackbar.showSuccessMessage('Note permanently deleted');
    } finally {
      this.setBusy(item._id, false);
    }
  }

  // ---------------------------------------------------------------- bulk actions

  async restoreSelected(): Promise<void> {
    const ids = [...this.selectedIds()];
    if (!ids.length || this.bulkBusy()) return;
    this.bulkBusy.set(true);
    try {
      const resp = await firstValueFrom(this.dataService.restoreManyFromTrash(ids));
      this.applyBulkResult(resp, resp.data?.restoredNoteIds ?? [], {
        done: (n) => (n === 1 ? 'Note restored' : `${n} notes restored`),
        failedVerb: 'restored',
        fallbackError: 'Restoring the notes failed.',
      });
      if ((resp.data?.restoredNoteIds ?? []).length) this.pullRestoredNotes();
    } finally {
      this.bulkBusy.set(false);
    }
  }

  confirmDeleteSelected(): void {
    const count = this.selectedCount();
    if (!count || this.bulkBusy()) return;
    this.confirmation.confirm({
      header: 'Delete permanently',
      message: `Permanently delete ${count} selected ${count === 1 ? 'note' : 'notes'}? This action cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete permanently',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => void this.deleteSelected(),
    });
  }

  async deleteSelected(): Promise<void> {
    const ids = [...this.selectedIds()];
    if (!ids.length || this.bulkBusy()) return;
    this.bulkBusy.set(true);
    try {
      const resp = await firstValueFrom(this.dataService.permanentlyDeleteManyFromTrash(ids));
      this.applyBulkResult(resp, resp.data?.deletedNoteIds ?? [], {
        done: (n) => (n === 1 ? 'Note permanently deleted' : `${n} notes permanently deleted`),
        failedVerb: 'deleted',
        fallbackError: 'Deleting the notes permanently failed.',
      });
    } finally {
      this.bulkBusy.set(false);
    }
  }

  confirmEmptyTrash(): void {
    if (!this.items().length || this.bulkBusy()) return;
    this.confirmation.confirm({
      header: 'Empty Trash',
      message: 'Permanently delete all notes in Trash? This action cannot be undone.',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Empty Trash',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => void this.emptyTrash(),
    });
  }

  async emptyTrash(): Promise<void> {
    if (this.bulkBusy()) return;
    this.bulkBusy.set(true);
    try {
      const resp = await firstValueFrom(this.dataService.emptyTrash());
      if (resp.status === 'error') {
        this.snackbar.showErrorMessage(resp.error?.message ?? 'Emptying Trash failed.');
        return;
      }
      const deleted = resp.data?.deletedNoteIds ?? [];
      this.removeItems(deleted);
      const failed = resp.data?.errors?.length ?? 0;
      if (failed) {
        this.snackbar.showErrorMessage(`Trash emptied, but ${failed} ${failed === 1 ? 'note' : 'notes'} could not be deleted.`);
        await this.load(); // show what is really left
      } else {
        this.items.set([]);
        this.clearSelection();
        this.snackbar.showSuccessMessage('Trash emptied');
      }
    } finally {
      this.bulkBusy.set(false);
    }
  }

  back(): void {
    void this.router.navigate(['/home'], { state: { refresh: true } });
  }

  // ---------------------------------------------------------------- helpers

  private applyBulkResult(
    resp: ApiResponse<TrashBulkResult>,
    doneIds: string[],
    text: { done: (n: number) => string; failedVerb: string; fallbackError: string },
  ): void {
    if (resp.status === 'error') {
      this.snackbar.showErrorMessage(resp.error?.message ?? text.fallbackError);
      return;
    }
    this.removeItems(doneIds);
    const failed = resp.data?.errors ?? [];
    if (failed.length) {
      const reason = failed[0]?.error ? ` (${failed[0].error})` : '';
      this.snackbar.showErrorMessage(
        `${doneIds.length ? text.done(doneIds.length) + '; ' : ''}${failed.length} could not be ${text.failedVerb}${reason}.`,
      );
    } else if (doneIds.length) {
      this.snackbar.showSuccessMessage(text.done(doneIds.length));
    }
  }

  private restoreErrorMessage(resp: ApiResponse<unknown>): string {
    const code = resp.error?.code;
    const message = resp.error?.message ?? 'Restoring the note failed.';
    if (code === 403) return `The note can’t be restored: you no longer have access to its Space. (${message})`;
    return message;
  }

  /** A restored note comes back to this device through the normal incremental sync pull. */
  private pullRestoredNotes(): void {
    void this.syncCoordinator.forceIncrementalSync('manual').catch(() => {
      /* the periodic sync will pick it up */
    });
  }

  private removeItems(ids: string[]): void {
    if (!ids.length) return;
    const gone = new Set(ids);
    this.items.update((items) => items.filter((item) => !gone.has(item._id)));
    this.pruneSelection();
  }

  /** Selection only ever refers to notes still listed. */
  private pruneSelection(): void {
    const present = new Set(this.items().map((item) => item._id));
    const selected = [...this.selectedIds()].filter((id) => present.has(id));
    if (selected.length !== this.selectedIds().size) this.selectedIds.set(new Set(selected));
  }

  private setBusy(id: string, busy: boolean): void {
    const next = new Set(this.busyIds());
    if (busy) next.add(id);
    else next.delete(id);
    this.busyIds.set(next);
  }
}
